import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '../Layout/Navbar';
import api from '../../services/api';
import './RunningCoach.css';

interface CoachMessage {
  id?: number;
  role: 'user' | 'assistant';
  content: string;
  actions_taken?: string[] | null;
  created_at?: string;
  pending?: boolean;
}

interface Insight {
  id: number;
  insight_type: string;
  body_part?: string;
  description: string;
  severity?: string;
  created_at: string;
}

const STARTER_PROMPTS = [
  "My right knee was sore during today's run",
  "I felt incredible on my run this morning",
  "Life's been hectic — I missed three workouts this week",
  "How should I adjust my plan for next week?",
  "I'm feeling fatigued and my legs are heavy",
];

const RunningCoach: React.FC = () => {
  const [messages, setMessages] = useState<CoachMessage[]>([]);
  const [insights, setInsights] = useState<Insight[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const autoScrollEnabled = useRef(false);

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    loadHistory();
    loadInsights();
    api.trackEvent('coach_viewed');

    // Prefill input from ?prefill=… (used by dashboard insight card and email link)
    const prefill = searchParams.get('prefill');
    if (prefill) {
      setInput(prefill);
      // Clear the param so refresh doesn't re-prefill
      searchParams.delete('prefill');
      setSearchParams(searchParams, { replace: true });
      // Focus the input on next tick
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Instant jump on initial load, smooth scroll for new messages
    bottomRef.current?.scrollIntoView({
      behavior: (autoScrollEnabled.current ? 'smooth' : 'instant') as ScrollBehavior
    });
  }, [messages]);

  const loadHistory = async () => {
    try {
      const data = await api.getCoachHistory();
      setMessages(data.messages);
    } finally {
      setHistoryLoaded(true);
      // After history is rendered (instant jump done), enable smooth scroll for new messages
      setTimeout(() => { autoScrollEnabled.current = true; }, 0);
    }
  };

  const clearTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const clearHistory = async () => {
    if (!confirmClear) {
      setConfirmClear(true);
      // Auto-reset after 3 seconds if user doesn't confirm
      clearTimeout(clearTimeoutRef.current);
      clearTimeoutRef.current = setTimeout(() => setConfirmClear(false), 3000);
      return;
    }
    clearTimeout(clearTimeoutRef.current);
    try {
      await api.clearCoachHistory();
      setMessages([]);
      setConfirmClear(false);
    } catch {
      setConfirmClear(false);
    }
  };

  const loadInsights = async () => {
    try {
      const data = await api.getCoachInsights();
      setInsights(data.insights);
    } catch {
      // non-critical
    }
  };

  const sendMessage = async (text?: string) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;

    setInput('');
    const userMsg: CoachMessage = { role: 'user', content: msg };
    const thinkingMsg: CoachMessage = { role: 'assistant', content: '', pending: true };

    setMessages(prev => [...prev, userMsg, thinkingMsg]);
    setLoading(true);

    try {
      const result = await api.sendCoachMessage(msg);
      setMessages(prev => {
        const next = prev.filter(m => !m.pending);
        return [
          ...next,
          {
            role: 'assistant',
            content: result.message,
            actions_taken: result.actions?.length ? result.actions : null,
            created_at: result.timestamp,
          },
        ];
      });
      // Refresh insights if coach took actions
      if (result.actions?.length) {
        loadInsights();
      }
    } catch (err: any) {
      setMessages(prev => {
        const next = prev.filter(m => !m.pending);
        return [
          ...next,
          {
            role: 'assistant',
            content: "Sorry, I ran into an issue. Please try again.",
          },
        ];
      });
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (iso?: string) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const severityColor = (s?: string) => {
    if (s === 'severe') return 'var(--error-red)';
    if (s === 'moderate') return 'var(--warning-yellow)';
    return 'var(--success-green)';
  };

  const isFirstSession = historyLoaded && messages.length === 0;

  return (
    <>
      <Navbar />
      <div className="coach-page">
        <div className="coach-layout">

          {/* Sidebar */}
          <aside className="coach-sidebar">
            <div className="sidebar-section">
              <h3 className="sidebar-heading">Your Coach</h3>
              <div className="coach-avatar-block">
                <div className="coach-avatar">🧠</div>
                <div>
                  <div className="coach-name">Running Brain</div>
                  <div className="coach-tagline">Adaptive AI Coach</div>
                </div>
              </div>
            </div>

            {insights.length > 0 && (
              <div className="sidebar-section">
                <h3 className="sidebar-heading">Active Flags</h3>
                <div className="insights-list">
                  {insights.map(ins => (
                    <div className="insight-chip" key={ins.id}>
                      <span
                        className="insight-dot"
                        style={{ background: severityColor(ins.severity) }}
                      />
                      <span className="insight-text">
                        {ins.body_part ? `${ins.body_part}: ` : ''}
                        {ins.description.length > 55
                          ? ins.description.slice(0, 55) + '…'
                          : ins.description}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {messages.length > 0 && (
              <div className="sidebar-section">
                <button
                  className={`clear-history-btn ${confirmClear ? 'confirm' : ''}`}
                  onClick={clearHistory}
                >
                  {confirmClear ? 'Tap again to confirm' : 'Clear conversation'}
                </button>
              </div>
            )}

            <div className="sidebar-section">
              <h3 className="sidebar-heading">Quick Topics</h3>
              <div className="quick-prompts">
                {STARTER_PROMPTS.map(p => (
                  <button
                    key={p}
                    className="quick-prompt-btn"
                    onClick={() => sendMessage(p)}
                    disabled={loading}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          </aside>

          {/* Chat area */}
          <main className="coach-chat">
            <div className="messages-area">
              {isFirstSession && (
                <div className="welcome-block">
                  <div className="welcome-icon">🧠</div>
                  <h2>Your Running Brain</h2>
                  <p>
                    Tell me how your training is going. Pain, fatigue, a great run, missed
                    workouts — anything that matters. I'll adjust your plan in real time and
                    build context about you as a runner over time.
                  </p>
                </div>
              )}

              {messages.map((msg, i) => (
                <div key={i} className={`message-row ${msg.role}`}>
                  {msg.role === 'assistant' && (
                    <div className="msg-avatar">🧠</div>
                  )}

                  <div className="msg-body">
                    {msg.pending ? (
                      <div className="msg-bubble assistant">
                        <div className="typing-dots">
                          <span /><span /><span />
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className={`msg-bubble ${msg.role}`}>
                          <MessageContent content={msg.content} />
                        </div>

                        {msg.actions_taken && msg.actions_taken.length > 0 && (
                          <div className="actions-row">
                            {msg.actions_taken.map((a, ai) => (
                              <span key={ai} className="action-chip">
                                ✓ {a}
                              </span>
                            ))}
                          </div>
                        )}

                        {msg.created_at && (
                          <div className={`msg-time ${msg.role}`}>
                            {formatTime(msg.created_at)}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {msg.role === 'user' && (
                    <div className="msg-avatar user-avatar">You</div>
                  )}
                </div>
              ))}

              <div ref={bottomRef} />
            </div>

            {/* Input */}
            <div className="input-area">
              <textarea
                ref={inputRef}
                className="chat-input"
                placeholder="Tell your coach what's going on…"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={1}
                disabled={loading}
              />
              <button
                className="send-btn"
                onClick={() => sendMessage()}
                disabled={loading || !input.trim()}
                aria-label="Send message"
              >
                {loading ? (
                  <div className="send-spinner" />
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </button>
            </div>
            <div className="input-hint">Enter to send · Shift+Enter for new line</div>
          </main>

        </div>
      </div>
    </>
  );
};

// Renders coach messages with basic markdown (bold, line breaks)
const MessageContent: React.FC<{ content: string }> = ({ content }) => {
  const lines = content.split('\n');
  return (
    <div className="msg-content">
      {lines.map((line, i) => {
        // Bold: **text**
        const parts = line.split(/(\*\*[^*]+\*\*)/g);
        return (
          <React.Fragment key={i}>
            {parts.map((part, j) =>
              part.startsWith('**') && part.endsWith('**')
                ? <strong key={j}>{part.slice(2, -2)}</strong>
                : <span key={j}>{part}</span>
            )}
            {i < lines.length - 1 && <br />}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default RunningCoach;
